import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { COVERAGE_OPTIONS, getUtm, postLead, track } from "../lib/api";

const CONCERNS = [
  { id: "accident", label: "Accident & unexpected injuries", maps: ["Accident"] },
  { id: "cancer", label: "Cancer / serious illness", maps: ["Cancer", "Critical Illness"] },
  { id: "heart", label: "Heart attack or stroke", maps: ["Heart / Stroke"] },
  { id: "hospital", label: "Hospital stay costs", maps: ["Hospital Indemnity"] },
  { id: "life", label: "Protecting my family's future", maps: ["Life Insurance"] },
  { id: "health", label: "Health coverage that makes sense", maps: ["Individual & Family Health"] },
  { id: "income", label: "Income disruption", maps: ["Accident", "Hospital Indemnity"] },
  { id: "unsure", label: "I'm not sure yet", maps: ["Not sure yet"] },
];

const STEPS = ["Your Concerns", "Areas to Review", "Where to Reach You"];

export const ShieldBuilder = () => {
  const navigate = useNavigate();
  const started = useRef(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [concerns, setConcerns] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [contact, setContact] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    zip_code: "",
    preferred_contact: "",
    concern: "",
    marketing_consent: false,
    company: "",
  });

  const markStarted = () => {
    if (!started.current) {
      started.current = true;
      track("shield_review_started");
    }
  };

  const toggleConcern = (c) => {
    markStarted();
    setConcerns((prev) =>
      prev.some((x) => x.id === c.id) ? prev.filter((x) => x.id !== c.id) : [...prev, c]
    );
  };

  const goStep2 = () => {
    const mapped = new Set(coverage);
    concerns.forEach((c) => c.maps.forEach((m) => mapped.add(m)));
    setCoverage([...mapped]);
    setStep(2);
  };

  const toggleCoverage = (c) =>
    setCoverage((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const set = (key) => (e) =>
    setContact({
      ...contact,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.first_name.trim() || !contact.phone.trim() || !contact.email.trim()) {
      toast.error("Please add your first name, phone, and email so we can reach you.");
      return;
    }
    setLoading(true);
    try {
      await postLead({
        ...contact,
        coverage_interests: coverage,
        preferred_contact: contact.preferred_contact || "Phone",
        source: "build-your-shield",
        form_type: "shield_review",
        ...getUtm(),
      });
      track("shield_review_completed");
      navigate("/thank-you");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Something went wrong sending that. Please call us instead."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="shield-builder" className="card-dark relative overflow-hidden !p-0">
      <div className="flex items-center justify-between border-b border-line px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-lime" />
          <span className="font-display text-sm font-bold uppercase tracking-wider text-white">
            Step {step} of 3 — {STEPS[step - 1]}
          </span>
        </div>
        <div className="hidden gap-1.5 sm:flex" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                i < step ? "bg-lime" : "bg-line"
              }`}
            />
          ))}
        </div>
      </div>
      <div className="h-1 bg-line">
        <div
          className="h-full bg-lime transition-[width] duration-500"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      <div className="p-5 sm:p-8">
        {step === 1 && (
          <div data-testid="shield-builder-step-1-chips">
            <h3 className="h-display text-2xl text-white sm:text-3xl">
              What would you like help protecting?
            </h3>
            <p className="mt-2 text-sm text-zinc-400">Pick anything on your mind. There&apos;s no wrong answer.</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {CONCERNS.map((c) => {
                const active = concerns.some((x) => x.id === c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`shield-concern-${c.id}`}
                    onClick={() => toggleConcern(c)}
                    aria-pressed={active}
                    className={`chip ${
                      active
                        ? "border-lime bg-lime text-ink"
                        : "border-line bg-ink-2 text-zinc-200 hover:border-lime/60"
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" />}
                    {c.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={goStep2}
              data-testid="shield-builder-next-1"
              className="btn-lime mt-8 w-full sm:w-auto"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="shield-builder-step-2-checklist">
            <h3 className="h-display text-2xl text-white sm:text-3xl">
              These are areas worth reviewing together.
            </h3>
            <p className="mt-2 text-sm text-zinc-400">
              Based on what you picked, here&apos;s where a conversation usually starts. Adjust freely.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {COVERAGE_OPTIONS.map((c) => {
                const active = coverage.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`shield-coverage-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleCoverage(c)}
                    aria-pressed={active}
                    className={`chip ${
                      active
                        ? "border-lime bg-lime text-ink"
                        : "border-line bg-ink-2 text-zinc-200 hover:border-lime/60"
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" />}
                    {c}
                  </button>
                );
              })}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-zinc-500">
              This tool is for educational purposes only. It does not determine eligibility, coverage,
              or provide an insurance recommendation.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => setStep(1)} data-testid="shield-builder-back-2" className="btn-ghost">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button onClick={() => setStep(3)} data-testid="shield-builder-next-2" className="btn-lime">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={submit} data-testid="shield-builder-step-3-form" className="space-y-4" noValidate>
            <input type="text" name="company" value={contact.company} onChange={set("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <h3 className="h-display text-2xl text-white sm:text-3xl">Where should we reach you?</h3>
            <p className="text-sm text-zinc-400">
              A real person reviews this and follows up the way you prefer.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="shield-form-first-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">First name</label>
                <input id="shield-form-first-name" data-testid="shield-form-first-name" className="input-field-dark" placeholder="First name" value={contact.first_name} onChange={set("first_name")} autoComplete="given-name" />
              </div>
              <div>
                <label htmlFor="shield-form-last-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Last name</label>
                <input id="shield-form-last-name" data-testid="shield-form-last-name" className="input-field-dark" placeholder="Last name" value={contact.last_name} onChange={set("last_name")} autoComplete="family-name" />
              </div>
              <div>
                <label htmlFor="shield-form-phone" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Phone</label>
                <input id="shield-form-phone" data-testid="shield-form-phone" type="tel" className="input-field-dark" placeholder="Phone" value={contact.phone} onChange={set("phone")} autoComplete="tel" />
              </div>
              <div>
                <label htmlFor="shield-form-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Email</label>
                <input id="shield-form-email" data-testid="shield-form-email" type="email" className="input-field-dark" placeholder="Email" value={contact.email} onChange={set("email")} autoComplete="email" />
              </div>
              <div>
                <label htmlFor="shield-form-zip" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">ZIP code</label>
                <input id="shield-form-zip" data-testid="shield-form-zip" inputMode="numeric" className="input-field-dark" placeholder="ZIP code" value={contact.zip_code} onChange={set("zip_code")} autoComplete="postal-code" />
              </div>
              <div>
                <label htmlFor="shield-form-preferred" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Preferred contact</label>
                <select id="shield-form-preferred" data-testid="shield-form-preferred" className="input-field-dark" value={contact.preferred_contact} onChange={set("preferred_contact")}>
                  <option value="">Choose one (optional)</option>
                  <option>Phone</option>
                  <option>Text</option>
                  <option>Email</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="shield-form-concern" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">What would you most like help understanding? (optional)</label>
              <textarea
                id="shield-form-concern"
                data-testid="shield-form-concern"
                rows={3}
                className="input-field-dark"
                placeholder="Tell us what's on your mind — in your own words."
                value={contact.concern}
                onChange={set("concern")}
              />
            </div>
            <label className="flex items-start gap-3 text-xs leading-relaxed text-zinc-400">
              <input type="checkbox" data-testid="shield-form-consent" checked={contact.marketing_consent} onChange={set("marketing_consent")} className="mt-0.5 h-4 w-4 shrink-0 accent-[#A8E600]" />
              It&apos;s okay to send me occasional helpful insurance information. Submitting does not
              obligate me to buy anything.
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setStep(2)} data-testid="shield-builder-back-3" className="btn-ghost">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button type="submit" data-testid="shield-builder-submit-button" disabled={loading} className="btn-lime flex-1 disabled:opacity-60">
                {loading ? "Sending..." : "Review My Shield"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <p className="text-center text-xs text-zinc-500">
              No pressure. No obligation. Just a conversation about your options.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};
